import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
} from "../../shared/ui";
import type { EmployeeSummary, LeaveType, PageResponse } from "../../shared/types/api";
import { LEAVE_TYPE_LABELS } from "./resourceVocabulary";

const TYPES = Object.keys(LEAVE_TYPE_LABELS) as LeaveType[];

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Records leave (§39).
 *
 * <p>The hours field is optional and says so. Left empty the leave costs whole working days,
 * which is what most leave is; filling it in expresses a half day without the model needing a
 * fractional-day concept that every other calculation would then have to understand.
 *
 * <p>The panel states plainly that recording is not approving. §39 makes approval the thing that
 * changes a capacity figure, and somebody who assumed otherwise would plan around time that is
 * still notionally available.
 */
export function LeaveRequestDrawer({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [employeeId, setEmployeeId] = useState("");
  const [leaveStart, setLeaveStart] = useState(today());
  const [leaveEnd, setLeaveEnd] = useState(today());
  const [leaveType, setLeaveType] = useState<LeaveType>("ANNUAL");
  const [leaveHours, setLeaveHours] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setEmployeeId("");
      setLeaveStart(today());
      setLeaveEnd(today());
      setLeaveType("ANNUAL");
      setLeaveHours("");
      setReason("");
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/v1/leave", {
        employeeId,
        leaveStart,
        leaveEnd,
        leaveType,
        leaveHours: leaveHours === "" ? null : Number(leaveHours),
        reason: reason || null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not record that leave.");
    } finally {
      setSaving(false);
    }
  };

  const backwards = leaveEnd < leaveStart;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Record leave"
      subtitle="Time somebody will be away."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={!employeeId || backwards}
          >
            {saving ? "Recording…" : "Record leave"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="Person" required>
          <Select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)}>
            <option value="">Choose a person</option>
            {(employees.data?.content ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="First day away" required>
            <Input
              type="date"
              value={leaveStart}
              onChange={(event) => setLeaveStart(event.target.value)}
            />
          </Field>
          <Field label="Last day away" required>
            <Input
              type="date"
              value={leaveEnd}
              min={leaveStart}
              onChange={(event) => setLeaveEnd(event.target.value)}
            />
          </Field>
        </div>

        {backwards && (
          <InlineAlert tone="warning">Leave cannot end before it starts.</InlineAlert>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Type" required>
            <Select
              value={leaveType}
              onChange={(event) => setLeaveType(event.target.value as LeaveType)}
            >
              {TYPES.map((value) => (
                <option key={value} value={value}>
                  {LEAVE_TYPE_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Hours"
            hint="Only for a part day. Left empty it costs whole working days."
          >
            <Input
              type="number"
              min={0}
              step="0.5"
              placeholder="whole days"
              value={leaveHours}
              onChange={(event) => setLeaveHours(event.target.value)}
            />
          </Field>
        </div>

        <Field label="Note" hint="Optional.">
          <Textarea
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>

        <InlineAlert tone="info">
          Recording is not approving. This changes nobody's capacity until it is approved — §39 is
          explicit that only approved leave reduces what is available, so that a speculative
          request cannot quietly shrink a team's plan.
        </InlineAlert>
      </div>
    </Drawer>
  );
}
